import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Instagram, Upload } from "lucide-react";
import api from "../lib/api";
import Reveal from "../components/common/Reveal";
import { proxied } from "../lib/assets";

export default function Gallery() {
  const [images, setImages] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/gallery")
      .then((r) => setImages(r.data))
      .catch(() => setImages([]))
      .finally(() => setLoading(false));
  }, []);

  const categories = ["All", ...Array.from(new Set(images.map((i) => i.category)))];
  const shown = filter === "All" ? images : images.filter((i) => i.category === filter);

  return (
    <div data-testid="page-gallery">
      <section className="py-24 lg:py-32 px-6 lg:px-10 bg-ivory">
        <div className="max-w-[1500px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <Reveal className="lg:col-span-8">
            <div className="star-sep mb-8">Captured Escapes</div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-8xl font-light leading-[0.98]" data-testid="gallery-title">
              Real moments.<br />Real <span className="italic text-gold">memories.</span>
            </h1>
          </Reveal>
          <Reveal className="lg:col-span-4" delay={120}>
            <p className="text-lg text-muted leading-relaxed font-light">
              A blend of my own travels and the unforgettable experiences of LuxeTini Escapes clients. Every photo tells a story.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Filters */}
      <section className="px-6 lg:px-10">
        <div className="max-w-[1500px] mx-auto flex flex-wrap gap-2 border-b border-hairline py-6" data-testid="gallery-filters">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-5 h-10 text-[11px] uppercase tracking-[0.26em] border transition-colors ${
                filter === cat
                  ? "bg-ink text-ivory border-ink"
                  : "bg-transparent text-ink border-hairline hover:border-ink"
              }`}
              data-testid={`gallery-filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {cat}
            </button>
          ))}
        </div>
      </section>

      {/* Bento grid */}
      <section className="py-16 px-6 lg:px-10">
        <div className="max-w-[1500px] mx-auto">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="aspect-[3/4] bg-hairline animate-shimmer" />
              ))}
            </div>
          ) : (
            <div className="columns-2 md:columns-3 lg:columns-4 gap-4" data-testid="gallery-grid">
              {shown.map((img, i) => (
                <Reveal key={img.id} delay={(i % 6) * 60}>
                  <figure
                    className="tile mb-4 break-inside-avoid"
                    data-testid={`gallery-image-${img.id}`}
                    ref={(el) => { if (el) el._imgLoaded = false; }}
                  >
                    <img
                      src={proxied(img.url)}
                      alt={img.caption}
                      loading="lazy"
                      className="w-full h-auto object-cover"
                      onError={(e) => {
                        // Hide the whole tile if the image fails to load
                        const fig = e.currentTarget.closest("figure");
                        if (fig) fig.style.display = "none";
                      }}
                    />
                    <figcaption className="mt-2 flex items-center justify-between gap-3 text-xs text-muted">
                      <span className="font-light italic">{img.caption}{img.location ? ` · ${img.location}` : ""}</span>
                      <span className="uppercase tracking-[0.2em] text-gold shrink-0">{img.category}</span>
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Share */}
      <section className="py-24 lg:py-32 px-6 lg:px-10 bg-nightsky text-ivory">
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            <Upload size={32} strokeWidth={1} className="mx-auto text-gold mb-5" />
            <div className="star-sep justify-center mb-6"><span>Be part of the gallery</span></div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-ivory">
              Tag your escape. <span className="italic text-gold">Inspire someone else's.</span>
            </h2>
            <p className="mt-8 text-lg text-ivory/70 font-light leading-relaxed">
              Use our official hashtags when sharing your travels — <span className="text-gold">#MyLuxeEscape</span> &nbsp;<span className="text-gold">#LuxeTiniEscapes</span> — and you may be featured on our site and social channels.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row justify-center items-center gap-4">
              <a
                href="https://www.instagram.com/luxetiniescapes/"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-3 h-12 px-6 border border-ivory/40 text-ivory hover:bg-ivory hover:text-ink transition-colors text-[12px] uppercase tracking-[0.28em]"
                data-testid="gallery-instagram-cta"
              >
                <Instagram size={16} strokeWidth={1.5} /> Follow @luxetiniescapes
              </a>
              <Link
                to="/plan-your-escape"
                className="inline-flex items-center gap-3 h-12 px-6 bg-gold text-ink hover:bg-goldDeep transition-colors text-[12px] uppercase tracking-[0.28em]"
                data-testid="gallery-cta"
              >
                Start Planning <ArrowUpRight size={16} strokeWidth={1.5} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
